import React, { useState, useMemo, useEffect, use } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import { TrendingUp, TrendingDown, Building2, MapPin, Calendar, Users, DollarSign, FileText, Activity, Award, Target, Zap } from 'lucide-react';
import SideBar from '../../../../components/SideBar';
import NavBar from '../../../../components/NavBar';
import Footer from '../../../../components/Footer';
import { LOGGED_USER } from '../../../../variables/constants';
import axiosInstance from '../../../../variables/api/axiosInstance';

// Simulation de données - À remplacer par vos vraies données API
const generateMockData = () => {
    const mois = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const semaines = Array.from({ length: 52 }, (_, i) => `S${i + 1}`);
    const jours = Array.from({ length: 30 }, (_, i) => `J${i + 1}`);

    const agences = [
        { nom: 'Lomé Centre', ville: 'Lomé', compagnie: 'AXA' },
        { nom: 'Kara Nord', ville: 'Kara', compagnie: 'AXA' },
        { nom: 'Sokodé Ouest', ville: 'Sokodé', compagnie: 'NSIA' },
        { nom: 'Atakpamé', ville: 'Atakpamé', compagnie: 'NSIA' },
        { nom: 'Dapaong', ville: 'Dapaong', compagnie: 'SAHAM' },
    ];

    const compagnies = ['AXA', 'NSIA', 'SAHAM', 'ASKIA', 'SONAR'];

    return {
        mois: mois.map((m, i) => ({
            periode: m,
            production: Math.floor(Math.random() * 500000) + 300000,
            contrats: Math.floor(Math.random() * 150) + 50,
            primeNette: Math.floor(Math.random() * 400000) + 250000,
            commission: Math.floor(Math.random() * 50000) + 20000,
            objectif: 450000,
        })),
        semaines: semaines.slice(-12).map((s, i) => ({
            periode: s,
            production: Math.floor(Math.random() * 120000) + 60000,
            contrats: Math.floor(Math.random() * 40) + 15,
        })),
        jours: jours.map((j, i) => ({
            periode: j,
            production: Math.floor(Math.random() * 20000) + 5000,
            contrats: Math.floor(Math.random() * 10) + 2,
        })),
        agences: agences.map(a => ({
            ...a,
            production: Math.floor(Math.random() * 800000) + 200000,
            contrats: Math.floor(Math.random() * 200) + 50,
            tauxCommission: (Math.random() * 5 + 10).toFixed(2),
            croissance: (Math.random() * 40 - 10).toFixed(1),
        })),
        compagnies: compagnies.map(c => ({
            nom: c,
            production: Math.floor(Math.random() * 2000000) + 500000,
            partMarche: (Math.random() * 30 + 10).toFixed(1),
            contrats: Math.floor(Math.random() * 500) + 100,
        })),
        villes: ['Lomé', 'Kara', 'Sokodé', 'Atakpamé', 'Dapaong'].map(v => ({
            nom: v,
            production: Math.floor(Math.random() * 1500000) + 300000,
            nbAgences: Math.floor(Math.random() * 5) + 2,
        })),
    };
};

const DashboardProduction = () => {
    const [userRole, setUserRole] = useState(LOGGED_USER?.profil?.organisme?.typeOrganisme); // 'POOL' ou 'ASSURANCE'
    const [userCompagnie, setUserCompagnie] = useState('AXA');
    const [periode, setPeriode] = useState('MOIS'); // 'JOUR', 'SEMAINE', 'MOIS', 'ANNEE'
    const [ongletActif, setOngletActif] = useState('vue-ensemble');
    const [mounted, setMounted] = useState(false);
    const [compagnies, setCompagnies] = useState([]);

    const data = useMemo(() => generateMockData(), []);

    useEffect(() => {
        const org = LOGGED_USER?.profil?.organisme;
        if (org?.typeOrganisme === 'ASSURANCE') {
            setUserCompagnie(org?.raisonSociale);
        }
        setUserRole(org?.typeOrganisme);
        setMounted(true);
    }, []);

    useEffect(() => {
        axiosInstance.get(`/api/organisme/all/assurances-pool`)
            .then(response => {
                setCompagnies(response.data.map(c => ({
                    label: c.raisonSociale, value: c.code
                })));
            })
            .catch(error => {
                console.error("Erreur lors du chargement des compagnies", error);
            });
    }, []);

    const donneesFiltrées = useMemo(() => {
        let donnees = data[periode.toLowerCase() === 'annee' ? 'mois' : periode.toLowerCase() === 'jour' ? 'jours' : periode.toLowerCase() === 'semaine' ? 'semaines' : 'mois'];

        if (userRole === 'ASSURANCE') {
            // Filtrer par compagnie
            return {
                ...data,
                agences: data.agences.filter(a => a.compagnie === userCompagnie),
            };
        }
        return data;
    }, [periode, userRole, userCompagnie, data]);

    const statistiquesGlobales = useMemo(() => {
        const prodData = periode === 'MOIS' ? data.mois : periode === 'SEMAINE' ? data.semaines : data.jours;
        const totalProduction = prodData.reduce((sum, d) => sum + d.production, 0);
        const totalContrats = prodData.reduce((sum, d) => sum + d.contrats, 0);
        const moyenneParPeriode = totalProduction / prodData.length;
        const dernierePeriode = prodData[prodData.length - 1]?.production || 0;
        const avantDernierePeriode = prodData[prodData.length - 2]?.production || 1;
        const tendance = ((dernierePeriode - avantDernierePeriode) / avantDernierePeriode * 100).toFixed(1);

        return {
            totalProduction,
            totalContrats,
            moyenneParPeriode,
            tendance: parseFloat(tendance),
            tauxReussite: 87.5,
        };
    }, [periode, data]);

    const COULEURS = ['#FF6B9D', '#C44569', '#FFA07A', '#98D8C8', '#6C5CE7', '#FDCB6E', '#00B894', '#E17055'];

    const formatMontant = (montant) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(montant);
    };

    const StatCard = ({ titre, valeur, icon: Icon, tendance, color, delay }) => (
        <div
            className="stat-card"
            style={{
                animationDelay: `${delay}ms`,
                opacity: mounted ? 1 : 0,
            }}
        >
            <div className="stat-header">
                <div className="stat-icon" style={{ background: `${color}15`, color: color }}>
                    <Icon size={24} />
                </div>
                {tendance !== undefined && (
                    <div className={`stat-tendance ${tendance >= 0 ? 'positif' : 'negatif'}`}>
                        {tendance >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                        {Math.abs(tendance)}%
                    </div>
                )}
            </div>
            <div className="stat-titre">{titre}</div>
            <div className="stat-valeur" style={{ color: color }}>{valeur}</div>
        </div>
    );

    return (
        <div className="layout-wrapper layout-content-navbar  ">
            <div className="layout-container">
                <SideBar />

                {/* <!-- Layout container --> */}
                <div className="layout-page">
                    <NavBar />

                    {/* <!-- Content wrapper --> */}
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div className="dashboard-container">
                                {/* En-tête */}
                                <header className="dashboard-header">
                                    <div className="header-content">
                                        <div className="header-left">
                                            <div className="header-titre">
                                                <Activity size={32} strokeWidth={2.5} />
                                                <div>
                                                    <h1>Tableau de Bord Production</h1>
                                                    <p className="header-subtitle">Analytics & Performance en temps réel</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="header-right">
                                            <div className="controles-groupe">
                                                {/* <div className="controle-item">
                                                    <label>Rôle : {userRole}</label>
                                                    <select value={userRole} onChange={(e) => setUserRole(e.target.value)} className="select-custom">
                                                        <option value="POOL">🏢 Pool (Vue Globale)</option>
                                                        <option value="ASSURANCE">🏛️ ASSURANCE</option>
                                                    </select>
                                                </div> */}
                                                {userRole === 'POOL' ? (
                                                    <div className="controle-item">
                                                        <label>Organisme</label>
                                                        <select value={userCompagnie} onChange={(e) => setUserCompagnie(e.target.value)} className="select-custom">
                                                            {compagnies.map(comp => (
                                                                <option key={comp.value} value={comp.value}>{comp.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : (
                                                    <div className="controle-item">
                                                        <label>Compagnie</label>
                                                        <input style={{ color: "whitesmoke" }} className='form-control' type="text" value={userCompagnie} readOnly />
                                                    </div>
                                                )}
                                                <div className="controle-item">
                                                    <label>Période</label>
                                                    <select value={periode} onChange={(e) => setPeriode(e.target.value)} className="select-custom">
                                                        <option value="JOUR">📅 Par Jour</option>
                                                        <option value="SEMAINE">📆 Par Semaine</option>
                                                        <option value="MOIS">📊 Par Mois</option>
                                                        <option value="ANNEE">📈 Annuelle</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </header>

                                {/* Navigation par onglets */}
                                <nav className="onglets-nav" style={{ marginTop: "10px" }}>
                                    <button
                                        className={`onglet ${ongletActif === 'vue-ensemble' ? 'actif' : ''}`}
                                        onClick={() => setOngletActif('vue-ensemble')}
                                    >
                                        <Target size={18} />
                                        Vue d'Ensemble
                                    </button>
                                    <button
                                        className={`onglet ${ongletActif === 'performance' ? 'actif' : ''}`}
                                        onClick={() => setOngletActif('performance')}
                                    >
                                        <Award size={18} />
                                        Performance
                                    </button>
                                    <button
                                        className={`onglet ${ongletActif === 'geographie' ? 'actif' : ''}`}
                                        onClick={() => setOngletActif('geographie')}
                                    >
                                        <MapPin size={18} />
                                        Géographie
                                    </button>
                                    <button
                                        className={`onglet ${ongletActif === 'previsions' ? 'actif' : ''}`}
                                        onClick={() => setOngletActif('previsions')}
                                    >
                                        <Zap size={18} />
                                        Prévisions
                                    </button>
                                </nav>

                                {/* Contenu principal */}
                                <main className="dashboard-main">
                                    {ongletActif === 'vue-ensemble' && (
                                        <div className="onglet-contenu">
                                            {/* Cartes statistiques */}
                                            <div className="stats-grid">
                                                <StatCard
                                                    titre="Production Totale"
                                                    valeur={formatMontant(statistiquesGlobales.totalProduction)}
                                                    icon={DollarSign}
                                                    tendance={statistiquesGlobales.tendance}
                                                    color="#6C5CE7"
                                                    delay={0}
                                                />
                                                <StatCard
                                                    titre="Contrats Émis"
                                                    valeur={statistiquesGlobales.totalContrats.toLocaleString()}
                                                    icon={FileText}
                                                    tendance={12.5}
                                                    color="#00B894"
                                                    delay={100}
                                                />
                                                <StatCard
                                                    titre="Moyenne par Période"
                                                    valeur={formatMontant(statistiquesGlobales.moyenneParPeriode)}
                                                    icon={Calendar}
                                                    color="#FDCB6E"
                                                    delay={200}
                                                />
                                                <StatCard
                                                    titre="Taux de Réussite"
                                                    valeur={`${statistiquesGlobales.tauxReussite}%`}
                                                    icon={Award}
                                                    tendance={4.2}
                                                    color="#FF6B9D"
                                                    delay={300}
                                                />
                                            </div>

                                            {/* Graphique principal - Évolution */}
                                            <div className="graphique-card grande">
                                                <div className="card-header">
                                                    <h3>📈 Évolution de la Production</h3>
                                                    <div className="badge-periode">{periode}</div>
                                                </div>
                                                <ResponsiveContainer width="100%" height={400}>
                                                    <AreaChart data={periode === 'MOIS' ? data.mois : periode === 'SEMAINE' ? data.semaines : data.jours}>
                                                        <defs>
                                                            <linearGradient id="colorProduction" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#6C5CE7" stopOpacity={0.8} />
                                                                <stop offset="95%" stopColor="#6C5CE7" stopOpacity={0.1} />
                                                            </linearGradient>
                                                            <linearGradient id="colorObjectif" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#FF6B9D" stopOpacity={0.6} />
                                                                <stop offset="95%" stopColor="#FF6B9D" stopOpacity={0.05} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                                                        <XAxis dataKey="periode" stroke="#666" style={{ fontSize: '12px' }} />
                                                        <YAxis stroke="#666" style={{ fontSize: '12px' }} tickFormatter={(val) => `${(val / 1000).toFixed(0)}K`} />
                                                        <Tooltip
                                                            contentStyle={{
                                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                                border: 'none',
                                                                borderRadius: '12px',
                                                                boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                                                            }}
                                                            formatter={(value) => formatMontant(value)}
                                                        />
                                                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                                        <Area type="monotone" dataKey="production" stroke="#6C5CE7" strokeWidth={3} fillOpacity={1} fill="url(#colorProduction)" name="Production" />
                                                        {periode === 'MOIS' && <Area type="monotone" dataKey="objectif" stroke="#FF6B9D" strokeWidth={2} strokeDasharray="5 5" fillOpacity={1} fill="url(#colorObjectif)" name="Objectif" />}
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>

                                            {/* Graphiques secondaires */}
                                            <div className="graphiques-grid">
                                                <div className="graphique-card">
                                                    <div className="card-header">
                                                        <h3>📊 Répartition Mensuelle</h3>
                                                    </div>
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <BarChart data={data.mois.slice(-6)}>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                                                            <XAxis dataKey="periode" stroke="#666" />
                                                            <YAxis stroke="#666" tickFormatter={(val) => `${(val / 1000).toFixed(0)}K`} />
                                                            <Tooltip formatter={(value) => formatMontant(value)} />
                                                            <Bar dataKey="production" fill="#6C5CE7" radius={[8, 8, 0, 0]} />
                                                            <Bar dataKey="commission" fill="#FDCB6E" radius={[8, 8, 0, 0]} />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>

                                                <div className="graphique-card">
                                                    <div className="card-header">
                                                        <h3>🥧 Part de Marché {userRole === 'POOL' ? 'par Compagnie' : 'par Agence'}</h3>
                                                    </div>
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <PieChart>
                                                            <Pie
                                                                data={userRole === 'POOL' ? data.compagnies : donneesFiltrées.agences}
                                                                cx="50%"
                                                                cy="50%"
                                                                labelLine={false}
                                                                label={({ nom, partMarche, production }) => `${nom} (${partMarche || ((production / data.compagnies.reduce((s, c) => s + c.production, 0) * 100).toFixed(1))}%)`}
                                                                outerRadius={100}
                                                                fill="#8884d8"
                                                                dataKey="production"
                                                            >
                                                                {(userRole === 'POOL' ? data.compagnies : donneesFiltrées.agences).map((entry, index) => (
                                                                    <Cell key={`cell-${index}`} fill={COULEURS[index % COULEURS.length]} />
                                                                ))}
                                                            </Pie>
                                                            <Tooltip formatter={(value) => formatMontant(value)} />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {ongletActif === 'performance' && (
                                        <div className="onglet-contenu">
                                            <div className="graphique-card grande">
                                                <div className="card-header">
                                                    <h3>🏆 Performance des {userRole === 'POOL' ? 'Agences' : 'Agences de ' + userCompagnie}</h3>
                                                </div>
                                                <div className="tableau-performance">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Rang</th>
                                                                <th>Agence</th>
                                                                <th>Ville</th>
                                                                {userRole === 'POOL' && <th>Compagnie</th>}
                                                                <th>Production</th>
                                                                <th>Contrats</th>
                                                                <th>Taux Commission</th>
                                                                <th>Croissance</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(userRole === 'POOL' ? data.agences : donneesFiltrées.agences)
                                                                ?.sort((a, b) => b.production - a.production)
                                                                ?.map((agence, index) => (
                                                                    <tr key={index} className="ligne-performance">
                                                                        <td>
                                                                            <div className={`rang-badge rang-${index + 1}`}>
                                                                                {index + 1 <= 3 ? '🏅' : index + 1}
                                                                            </div>
                                                                        </td>
                                                                        <td className="nom-agence">{agence.nom}</td>
                                                                        <td><MapPin size={14} /> {agence.ville}</td>
                                                                        {userRole === 'POOL' && <td><Building2 size={14} /> {agence.compagnie}</td>}
                                                                        <td className="montant-prod">{formatMontant(agence.production)}</td>
                                                                        <td>{agence.contrats}</td>
                                                                        <td>{agence.tauxCommission}%</td>
                                                                        <td>
                                                                            <div className={`croissance ${parseFloat(agence.croissance) >= 0 ? 'positif' : 'negatif'}`}>
                                                                                {parseFloat(agence.croissance) >= 0 ? '↗' : '↘'} {Math.abs(agence.croissance)}%
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>

                                            <div className="graphiques-grid">
                                                <div className="graphique-card">
                                                    <div className="card-header">
                                                        <h3>📊 Top 5 Agences</h3>
                                                    </div>
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <BarChart
                                                            data={(userRole === 'POOL' ? data.agences : donneesFiltrées.agences)
                                                                ?.sort((a, b) => b.production - a.production)
                                                                ?.slice(0, 5)}
                                                            layout="vertical"
                                                        >
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                                                            <XAxis type="number" tickFormatter={(val) => `${(val / 1000).toFixed(0)}K`} />
                                                            <YAxis dataKey="nom" type="category" width={100} />
                                                            <Tooltip formatter={(value) => formatMontant(value)} />
                                                            <Bar dataKey="production" fill="#00B894" radius={[0, 8, 8, 0]} />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>

                                                <div className="graphique-card">
                                                    <div className="card-header">
                                                        <h3>🎯 Taux de Conversion</h3>
                                                    </div>
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <RadialBarChart
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius="20%"
                                                            outerRadius="90%"
                                                            data={[
                                                                { name: 'Devis', value: 75, fill: '#FDCB6E' },
                                                                { name: 'Contrats', value: 87.5, fill: '#00B894' },
                                                                { name: 'Règlements', value: 92, fill: '#6C5CE7' },
                                                            ]}
                                                        >
                                                            <RadialBar dataKey="value" />
                                                            <Legend iconSize={10} layout="vertical" verticalAlign="middle" align="right" />
                                                            <Tooltip />
                                                        </RadialBarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {ongletActif === 'geographie' && (
                                        <div className="onglet-contenu">
                                            <div className="graphique-card grande">
                                                <div className="card-header">
                                                    <h3>🗺️ Performance par Ville</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={400}>
                                                    <BarChart data={data?.villes?.sort((a, b) => b.production - a.production)}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                                                        <XAxis dataKey="nom" stroke="#666" />
                                                        <YAxis stroke="#666" tickFormatter={(val) => `${(val / 1000).toFixed(0)}K`} />
                                                        <Tooltip
                                                            formatter={(value) => formatMontant(value)}
                                                            contentStyle={{
                                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                                border: 'none',
                                                                borderRadius: '12px',
                                                                boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                                                            }}
                                                        />
                                                        <Legend />
                                                        <Bar dataKey="production" fill="#FF6B9D" radius={[8, 8, 0, 0]} name="Production" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>

                                            <div className="graphiques-grid">
                                                {data.villes.map((ville, index) => (
                                                    <div key={index} className="ville-card">
                                                        <div className="ville-header">
                                                            <MapPin size={24} />
                                                            <h4>{ville.nom}</h4>
                                                        </div>
                                                        <div className="ville-stats">
                                                            <div className="ville-stat">
                                                                <span className="label">Production</span>
                                                                <span className="valeur">{formatMontant(ville.production)}</span>
                                                            </div>
                                                            <div className="ville-stat">
                                                                <span className="label">Agences</span>
                                                                <span className="valeur">{ville.nbAgences}</span>
                                                            </div>
                                                            <div className="ville-stat">
                                                                <span className="label">Moy. / Agence</span>
                                                                <span className="valeur">{formatMontant(ville.production / ville.nbAgences)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {ongletActif === 'previsions' && (
                                        <div className="onglet-contenu">
                                            <div className="graphique-card grande">
                                                <div className="card-header">
                                                    <h3>🔮 Prévisions & Tendances</h3>
                                                    <div className="badge-info">Basé sur les 12 derniers mois</div>
                                                </div>
                                                <ResponsiveContainer width="100%" height={400}>
                                                    <LineChart data={[
                                                        ...data.mois,
                                                        { periode: 'Prév J+1', production: data.mois[data.mois.length - 1].production * 1.08, prevision: true },
                                                        { periode: 'Prév J+2', production: data.mois[data.mois.length - 1].production * 1.12, prevision: true },
                                                        { periode: 'Prév J+3', production: data.mois[data.mois.length - 1].production * 1.15, prevision: true },
                                                    ]}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                                                        <XAxis dataKey="periode" stroke="#666" />
                                                        <YAxis stroke="#666" tickFormatter={(val) => `${(val / 1000).toFixed(0)}K`} />
                                                        <Tooltip formatter={(value) => formatMontant(value)} />
                                                        <Legend />
                                                        <Line type="monotone" dataKey="production" stroke="#6C5CE7" strokeWidth={3} dot={{ r: 5 }} name="Production Réelle" />
                                                        <Line type="monotone" dataKey="production" stroke="#FF6B9D" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} name="Prévisions" connectNulls />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>

                                            <div className="previsions-insights">
                                                <div className="insight-card">
                                                    <Zap size={32} />
                                                    <h4>Croissance Prévue</h4>
                                                    <div className="insight-valeur">+15.2%</div>
                                                    <p>Pour le prochain trimestre</p>
                                                </div>
                                                <div className="insight-card">
                                                    <Target size={32} />
                                                    <h4>Objectif Atteignable</h4>
                                                    <div className="insight-valeur">94%</div>
                                                    <p>Probabilité d'atteinte</p>
                                                </div>
                                                <div className="insight-card">
                                                    <TrendingUp size={32} />
                                                    <h4>Meilleure Période</h4>
                                                    <div className="insight-valeur">Décembre</div>
                                                    <p>Pic de production annuel</p>
                                                </div>
                                                <div className="insight-card">
                                                    <Award size={32} />
                                                    <h4>Performance Globale</h4>
                                                    <div className="insight-valeur">Excellente</div>
                                                    <p>Au-dessus des attentes</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </main>

                                <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .dashboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9ff 0%, #e8ecff 100%);
          font-family: 'Outfit', sans-serif;
          color: #2d3436;
        }

        .dashboard-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem 2.5rem;
          box-shadow: 0 8px 32px rgba(102, 126, 234, 0.25);
          position: sticky;
          top: 0;
          z-index: 100;
          backdrop-filter: blur(10px);
        }

        .header-content {
          max-width: 1600px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .header-titre {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-titre h1 {
          font-size: 2rem;
          font-weight: 800;
          letter-spacing: -0.5px;
          margin: 0;
        }

        .header-subtitle {
          font-size: 0.95rem;
          opacity: 0.9;
          font-weight: 400;
          margin-top: 0.25rem;
        }

        .controles-groupe {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .controle-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .controle-item label {
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          opacity: 0.9;
        }

        .select-custom {
          padding: 0.75rem 1.25rem;
          background: rgba(255, 255, 255, 0.25);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          font-family: 'Outfit', sans-serif;
        }

        .select-custom:hover {
          background: rgba(255, 255, 255, 0.35);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .select-custom option {
          background: #2d3436;
          color: white;
        }

        .onglets-nav {
          max-width: 1600px;
          margin: -1.5rem auto 0;
          padding: 0 2.5rem;
          display: flex;
          gap: 0.5rem;
          position: relative;
          z-index: 50;
        }

        .onglet {
          padding: 1rem 1.75rem;
          background: white;
          border: none;
          border-radius: 16px 16px 0 0;
          font-size: 0.95rem;
          font-weight: 600;
          color: #636e72;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
          font-family: 'Outfit', sans-serif;
        }

        .onglet:hover {
          background: #f8f9ff;
          transform: translateY(-2px);
        }

        .onglet.actif {
          background: white;
          color: #667eea;
          box-shadow: 0 -8px 24px rgba(102, 126, 234, 0.15);
          transform: translateY(-4px);
        }

        .dashboard-main {
          max-width: 1600px;
          margin: 0 auto;
          padding: 2rem 2.5rem;
        }

        .onglet-contenu {
          animation: fadeInUp 0.5s ease;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.75rem;
          border-radius: 20px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          animation: slideIn 0.5s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .stat-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stat-tendance {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.5rem 0.85rem;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 700;
        }

        .stat-tendance.positif {
          background: #d4edda;
          color: #00B894;
        }

        .stat-tendance.negatif {
          background: #f8d7da;
          color: #e17055;
        }

        .stat-titre {
          font-size: 0.9rem;
          color: #636e72;
          font-weight: 500;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-valeur {
          font-size: 2rem;
          font-weight: 800;
          font-family: 'Space Mono', monospace;
          letter-spacing: -1px;
        }

        .graphique-card {
          background: white;
          border-radius: 20px;
          padding: 2rem;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }

        .graphique-card:hover {
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
        }

        .graphique-card.grande {
          margin-bottom: 2rem;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f0f0f0;
        }

        .card-header h3 {
          font-size: 1.35rem;
          font-weight: 700;
          color: #2d3436;
        }

        .badge-periode,
        .badge-info {
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .badge-info {
          background: linear-gradient(135deg, #FDCB6E 0%, #E17055 100%);
        }

        .graphiques-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
          gap: 1.5rem;
        }

        .tableau-performance {
          overflow-x: auto;
          border-radius: 12px;
        }

        .tableau-performance table {
          width: 100%;
          border-collapse: collapse;
        }

        .tableau-performance th {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1rem 1.25rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .tableau-performance th:first-child {
          border-radius: 12px 0 0 0;
        }

        .tableau-performance th:last-child {
          border-radius: 0 12px 0 0;
        }

        .tableau-performance td {
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.95rem;
        }

        .ligne-performance {
          transition: all 0.3s ease;
        }

        .ligne-performance:hover {
          background: #f8f9ff;
          transform: scale(1.01);
        }

        .rang-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .rang-1 {
          background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
          color: white;
        }

        .rang-2 {
          background: linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%);
          color: white;
        }

        .rang-3 {
          background: linear-gradient(135deg, #CD7F32 0%, #B8860B 100%);
          color: white;
        }

        .nom-agence {
          font-weight: 600;
          color: #2d3436;
        }

        .montant-prod {
          font-weight: 700;
          color: #6C5CE7;
          font-family: 'Space Mono', monospace;
        }

        .croissance {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.9rem;
        }

        .croissance.positif {
          background: #d4edda;
          color: #00B894;
        }

        .croissance.negatif {
          background: #f8d7da;
          color: #e17055;
        }

        .ville-card {
          background: white;
          border-radius: 16px;
          padding: 1.5rem;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }

        .ville-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
        }

        .ville-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.25rem;
          color: #6C5CE7;
        }

        .ville-header h4 {
          font-size: 1.2rem;
          font-weight: 700;
        }

        .ville-stats {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }

        .ville-stat {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: #f8f9ff;
          border-radius: 10px;
        }

        .ville-stat .label {
          font-size: 0.85rem;
          color: #636e72;
          font-weight: 500;
        }

        .ville-stat .valeur {
          font-size: 1rem;
          font-weight: 700;
          color: #2d3436;
          font-family: 'Space Mono', monospace;
        }

        .previsions-insights {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .insight-card {
          background: white;
          border-radius: 20px;
          padding: 2rem;
          text-align: center;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .insight-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .insight-card svg {
          color: #6C5CE7;
          margin-bottom: 1rem;
        }

        .insight-card h4 {
          font-size: 1rem;
          color: #636e72;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }

        .insight-valeur {
          font-size: 2.5rem;
          font-weight: 800;
          color: #6C5CE7;
          margin-bottom: 0.5rem;
          font-family: 'Space Mono', monospace;
        }

        .insight-card p {
          font-size: 0.9rem;
          color: #636e72;
        }

        @media (max-width: 1200px) {
          .graphiques-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .dashboard-header {
            padding: 1.5rem;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-titre h1 {
            font-size: 1.5rem;
          }

          .onglets-nav {
            padding: 0 1rem;
            flex-wrap: wrap;
          }

          .dashboard-main {
            padding: 1.5rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .tableau-performance {
            font-size: 0.85rem;
          }
        }
      `}</style>
                            </div>
                        </div>
                        <Footer />
                        {/* <!-- / Footer --> */}

                        <div className="content-backdrop fade"></div>
                    </div>
                    {/* <!-- Content wrapper --> */}
                </div>
                {/* <!-- / Layout page --> */}
            </div>

            {/* <!-- Overlay --> */}
            <div className="layout-overlay layout-menu-toggle"></div>

            {/* <!-- Drag Target Area To SlideIn Menu On Small Screens --> */}
            <div className="drag-target"></div>
        </div >
    );
};

export default DashboardProduction;